import {
  Box,
  Alert,
  AlertIcon,
  AlertDescription,
  Heading,
  Text,
  Skeleton,
  Flex,
  Grid,
  GridItem,
  Button,
  useToast,
  Link,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { SuccessPortfolioCovenantGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { PortfolioCovenantQueryInput } from './AddQueryInputModal';
import {
  getSettingsPortfolioCovenantsBreadcrumb,
  SettingsBreadcrumb,
  SettingsFacilitiesBreadcrumb,
} from './SettingsBreadcrumbs';
import {
  getQueryInputsFromWipConfig,
  PortfolioCovenantEditTab,
  PortfolioCovenantsSidebarMenu,
} from './SettingsFacilityPortfolioCovenantsSidebar';
import {
  useGetPortfolioCovenantByIdLazyQueryGQLType,
  useGetSuccessPortfolioCovenantsByKeyAndCreditAgreementIdQuery,
} from 'packages/borrowing-base/requests/portfolioCovenants.gql.generated';
import { colors } from 'packages/common-frontend/colors';
import { Breadcrumbs } from 'packages/common-frontend/components/Breadcrumbs';
import { Icon } from 'packages/common-frontend/components/icons';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { useEvaluatePortfolioCovenantsMutation } from 'packages/creditAgreements/requests/requests.gql.generated';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { SettingsFacilityPortfolioCovenantsPreviewDrawer } from 'packages/settings/ui/SettingsFacilityPortfolioCovenantsPreviewDrawer';
import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';

const RouteParamsStruct = z.object({
  creditFacilityId: z
    .string({ message: 'URL parameter must be a string' })
    .optional(),
});

export const SettingsFacilityPortfolioCovenantsNoCovenantKeySelected = ({
  creditFacilityId,
}: {
  creditFacilityId: string;
}) => {
  return (
    <Alert status="info" m="24px">
      <AlertIcon />
      <AlertDescription>
        <Flex>
          <Text>Select&nbsp;</Text>
          <Link
            as={RouterLink}
            to={`/settings/credit-facilities/${creditFacilityId}/portfolio-covenants/configuration`}
            style={{ textDecoration: 'underline' }}
          >
            a portfolio covenant
          </Link>
          <Text>&nbsp;to edit.</Text>
        </Flex>
      </AlertDescription>
    </Alert>
  );
};

type CovenantKeyDropdownSelectProps = {
  allCovenantKeys: string[];
  selectedCovenantKey: string;
  setSelectedTab: React.Dispatch<
    React.SetStateAction<PortfolioCovenantEditTab>
  >;
};
const CovenantKeyDropdownSelect = ({
  allCovenantKeys,
  selectedCovenantKey,
  setSelectedTab,
}: CovenantKeyDropdownSelectProps) => {
  const navigate = useNavigate();

  const params = useParams();
  const { creditFacilityId } = RouteParamsStruct.parse(params);

  const allCovenantKeyOptions = _.filter(
    allCovenantKeys,
    (covenantKey) => covenantKey !== selectedCovenantKey,
  );

  return (
    <Flex alignItems="center">
      <Heading
        as="h1"
        mt="16px"
        mb="16px"
        fontSize="36px"
        fontWeight={600}
        mr="16px"
      >
        {selectedCovenantKey}
      </Heading>
      {!!allCovenantKeyOptions.length && (
        <Menu>
          <MenuButton as={Button} variant="ghost">
            <Icon icon="chevron-down" />
          </MenuButton>
          <MenuList>
            {allCovenantKeyOptions.map((covenantKey) => (
              <MenuItem
                key={`covenant-key-selector-menu-item-${covenantKey}`}
                onClick={() => {
                  setSelectedTab('properties');
                  navigate(
                    `/settings/credit-facilities/${creditFacilityId}/portfolio-covenants/edit?covenantKey=${covenantKey}`,
                  );
                }}
              >
                {covenantKey}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      )}
    </Flex>
  );
};

type SettingsFacilityPortfolioCovenantsEditTemplateProps = {
  creditFacilityId: string;
  selectedTab: PortfolioCovenantEditTab;
  setSelectedTab: React.Dispatch<
    React.SetStateAction<PortfolioCovenantEditTab>
  >;
  covenantKey: string;
  triggerDate: Util.FullDateStr;
  setTriggerDate: React.Dispatch<React.SetStateAction<Util.FullDateStr>>;
  wipConfig: WipConfig;
  children: React.ReactNode;
  selectedQueryInput: PortfolioCovenantQueryInput | null;
  setSelectedQueryInput: React.Dispatch<
    React.SetStateAction<PortfolioCovenantQueryInput | null>
  >;
  fullPropertyKey: string;
  portfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
  allPortfolioCovenantKeys: string[];
};
export const SettingsFacilityPortfolioCovenantsEditTemplate = ({
  creditFacilityId,
  selectedTab,
  setSelectedTab,
  covenantKey,
  triggerDate,
  setTriggerDate,
  wipConfig,
  selectedQueryInput,
  setSelectedQueryInput,
  fullPropertyKey,
  portfolioCovenant,
  allPortfolioCovenantKeys,
  children,
}: SettingsFacilityPortfolioCovenantsEditTemplateProps) => {
  const toast = useToast();
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {
    isOpen: isNavigationSidebarOpen,
    onOpen: onNavigationSidebarOpen,
    onClose: onNavigationSidebarClose,
  } = useDisclosure({ defaultIsOpen: true });

  const {
    isOpen: isPreviewResultDrawerOpen,
    onOpen: onPreviewResultDrawerOpen,
    onClose: onPreviewResultDrawerClose,
  } = useDisclosure();

  const [
    latestPortfolioCovenantPreviewResult,
    setLatestPortfolioCovenantPreviewResult,
  ] = useState<SuccessPortfolioCovenantGQLType | null>(null);
  const [
    isPortfolioCovenantPreviewResultLoading,
    setIsPortfolioCovenantPreviewResultLoading,
  ] = useState<boolean>(false);

  const {
    data: creditFacilityData,
    loading: isCreditFacilityLoading,
  } = useGetCreditFacilityQuery({ variables: { creditFacilityId } });

  const {
    data: successPortfolioCovenantsByKeyData,
    loading: isSuccessPortfolioCovenantsByKeyLoading,
    refetch: refetchSuccessfulPortfolioCovenantsByKey,
  } = useGetSuccessPortfolioCovenantsByKeyAndCreditAgreementIdQuery({
    variables: {
      creditFacilityId,
      creditAgreementId: getIn(wipConfig.formProps.values, 'id'),
      covenantKey,
      mode: 'QA',
    },
    onCompleted: (data) => {
      if (
        data.getSuccessPortfolioCovenantsByKeyAndCreditAgreementId &&
        data.getSuccessPortfolioCovenantsByKeyAndCreditAgreementId.length
      ) {
        setLatestPortfolioCovenantPreviewResult(
          data.getSuccessPortfolioCovenantsByKeyAndCreditAgreementId[0], // accessor currently orders by latest reportingDate + fileVersion, but perhaps we should order by updated_at instead to show latest-ran previews first by user timestamp
        );
      }
    },
    fetchPolicy: 'network-only',
  });

  const [
    getPortfolioCovenantById,
    { stopPolling: stopGetPortfolioCovenantByIdPolling },
  ] = useGetPortfolioCovenantByIdLazyQueryGQLType({
    fetchPolicy: 'network-only',
    pollInterval: 5000,
    onError: () => {
      toast({
        ...errorToastConfig,
        title: 'Portfolio Covenant Evaluation',
        description: 'Unable to complete portfolio covenant evaluation.',
      });
    },
    onCompleted: (data) => {
      // if portfolio covenant evaluation has not completed (ie. status === 'initialized),
      // the resolver will return null and we will continue polling

      // if portfolio covenant evaluation has completed (ie. status === "success" or "error")
      if (data.getPortfolioCovenantById) {
        // if portfolio covenant evaluation is successful, stop polling and set result
        if (
          data.getPortfolioCovenantById.__typename ===
            'SuccessPortfolioCovenant' &&
          data.getPortfolioCovenantById.status === 'success'
        ) {
          setIsPortfolioCovenantPreviewResultLoading(false);

          if (stopGetPortfolioCovenantByIdPolling) {
            stopGetPortfolioCovenantByIdPolling();
          }

          setLatestPortfolioCovenantPreviewResult(
            data.getPortfolioCovenantById,
          );

          onPreviewResultDrawerOpen();

          // refetch all history including latest one
          refetchSuccessfulPortfolioCovenantsByKey();
        } else if (
          data.getPortfolioCovenantById.__typename ===
            'ErrorPortfolioCovenant' &&
          data.getPortfolioCovenantById.status === 'error'
        ) {
          // if portfolio covenant evaluation is error, stop polling and show error
          setIsPortfolioCovenantPreviewResultLoading(false);

          if (stopGetPortfolioCovenantByIdPolling) {
            stopGetPortfolioCovenantByIdPolling();
          }

          const errors = data.getPortfolioCovenantById.metadata?.errors;

          toast({
            ...errorToastConfig,
            title: 'Portfolio Covenant Evaluation Error',
            description: errors ? errors.join(',') : undefined,
          });
        }
      }
    },
    notifyOnNetworkStatusChange: true,
  });

  const [
    evaluatePortfolioCovenants,
    { loading: isEvaluatePortfolioCovenantsQALoading },
  ] = useEvaluatePortfolioCovenantsMutation({
    onCompleted: () => {
      toast({
        ...successToastConfig,
        title: 'Portfolio Covenant Evaluation',
        description: 'Initiated portfolio covenant evaluation.',
      });
    },
    onError: (error) => {
      toast({
        ...errorToastConfig,
        title: 'Portfolio Covenant Evaluation',
        description: getErrorMessageFromApolloError({
          error,
          defaultMessage:
            'Unable to initiate portfolio covenants evaluation. Please see technical assistance.',
        }),
        isClosable: true,
      });

      setIsPortfolioCovenantPreviewResultLoading(false);
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const handleEvaluatePortfolioCovenantsQA = useCallback(
    async (wc: WipConfig) => {
      const creditAgreementId = getIn(wc.formProps.values, 'id');

      if (covenantKey) {
        setIsPortfolioCovenantPreviewResultLoading(true);

        // clear previous preview run
        setLatestPortfolioCovenantPreviewResult(null);

        const result = await evaluatePortfolioCovenants({
          variables: {
            evaluatePortfolioCovenantsRequest: {
              covenantKeys: [covenantKey],
              creditAgreementId,
              creditFacilityId,
              triggerDate,
              mode: 'QA',
            },
          },
        });

        const resultPortfolioCovenantIds =
          result.data?.evaluatePortfolioCovenants.portfolioCovenantIds;
        finleyAssert(
          resultPortfolioCovenantIds && resultPortfolioCovenantIds.length === 1,
          `There must be exactly one portfolio covenant worklease result, instead received ${
            resultPortfolioCovenantIds?.length ?? 0
          }`,
        );
        const portfolioCovenantIdFromWorklease = resultPortfolioCovenantIds[0];

        getPortfolioCovenantById({
          variables: { portfolioCovenantId: portfolioCovenantIdFromWorklease },
        });
      }
    },
    [
      evaluatePortfolioCovenants,
      triggerDate,
      covenantKey,
      creditFacilityId,
      setIsPortfolioCovenantPreviewResultLoading,
      getPortfolioCovenantById,
    ],
  );

  if (
    !creditFacilityData ||
    isCreditFacilityLoading ||
    !successPortfolioCovenantsByKeyData ||
    isSuccessPortfolioCovenantsByKeyLoading
  ) {
    return (
      <Skeleton
        h="600px"
        data-testid="settings-facility-portfolio-covenants-edit-template-loading"
      />
    );
  }

  const queryInputs = getQueryInputsFromWipConfig(covenantKey, wipConfig);

  const breadcrumbs = [
    SettingsBreadcrumb,
    SettingsFacilitiesBreadcrumb,
    { label: creditFacilityData.creditFacility.name },
    getSettingsPortfolioCovenantsBreadcrumb(creditFacilityId),
    { label: 'Edit' },
  ];

  const totalGridColumns = isNavigationSidebarOpen ? 6 : 20;
  const gridTemplateColumns = `repeat(${totalGridColumns}, 1fr)`;
  const contentGridColspan = totalGridColumns - 1;

  return (
    <>
      {wipConfig && (
        <Box
          position="sticky"
          top="82px"
          zIndex="sticky"
          padding="21px"
          backgroundColor={colors.background[100]}
          boxShadow="0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.10)"
          borderTop="2px"
          borderTopColor={colors.gray[200]}
        >
          <Flex justifyContent="right" gap="1rem">
            <Flex alignItems="start" pt="0.5rem">
              <Typography fontWeight="medium">Preview Trigger Date</Typography>
            </Flex>
            <SimpleDatePicker
              dateFormat="MMMM dd, yyyy"
              selectedDate={commonDate.fullDateStrToDate(triggerDate)}
              setSelectedDate={(date: Date | null) => {
                if (!date) return;
                const fullDateStr = commonDate.dateToFullDateString(
                  date,
                  clientTimeZone,
                );
                setTriggerDate(fullDateStr);
              }}
            />

            {(latestPortfolioCovenantPreviewResult ||
              !!successPortfolioCovenantsByKeyData
                .getSuccessPortfolioCovenantsByKeyAndCreditAgreementId
                .length) && (
              <Box mr="24px">
                <Button
                  backgroundColor="white"
                  colorScheme="aurora"
                  variant="outline"
                  borderWidth="2px"
                  onClick={onPreviewResultDrawerOpen}
                >
                  View Preview
                </Button>
                <SettingsFacilityPortfolioCovenantsPreviewDrawer
                  creditAgreementPortfolioCovenant={portfolioCovenant}
                  allPortfolioCovenantPreviewResults={
                    successPortfolioCovenantsByKeyData.getSuccessPortfolioCovenantsByKeyAndCreditAgreementId
                  }
                  currentPortfolioCovenantPreviewResult={
                    latestPortfolioCovenantPreviewResult
                  }
                  portfolioCovenantFullPropertyKey={fullPropertyKey}
                  isOpen={isPreviewResultDrawerOpen}
                  onClose={onPreviewResultDrawerClose}
                  isLatestPreviewLoading={
                    isPortfolioCovenantPreviewResultLoading
                  }
                />
              </Box>
            )}
            <Button
              colorScheme="aurora"
              color="white"
              onClick={() => handleEvaluatePortfolioCovenantsQA(wipConfig)}
              isDisabled={
                isEvaluatePortfolioCovenantsQALoading ||
                isPortfolioCovenantPreviewResultLoading
              }
            >
              {isEvaluatePortfolioCovenantsQALoading ||
              isPortfolioCovenantPreviewResultLoading ? (
                <Flex alignItems="center">
                  <Spinner size="sm" mr="16px" />
                  <Text>Loading</Text>
                </Flex>
              ) : (
                <Text>
                  {latestPortfolioCovenantPreviewResult
                    ? 'Refresh Preview'
                    : 'Preview Covenant'}
                </Text>
              )}
            </Button>
          </Flex>
        </Box>
      )}
      <Box p="40px" maxH="calc(100vh - 250px)" overflow="auto">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <Box mt="16px">
          <CovenantKeyDropdownSelect
            allCovenantKeys={allPortfolioCovenantKeys}
            selectedCovenantKey={covenantKey}
            setSelectedTab={setSelectedTab}
          />
          <Flex>
            <Text fontSize="16px" mb="16px" color={colors.black[70]}>
              Manage portfolio covenants for&nbsp;
            </Text>
            <Text fontSize="16px" mb="16px" fontWeight={500}>
              {creditFacilityData.creditFacility.name}.
            </Text>
          </Flex>
        </Box>
        <Box mt="64px">
          <Grid templateColumns={gridTemplateColumns}>
            <GridItem colSpan={1}>
              <PortfolioCovenantsSidebarMenu
                wipConfig={wipConfig}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                queryInputs={queryInputs}
                selectedQueryInput={selectedQueryInput}
                setSelectedQueryInput={setSelectedQueryInput}
                portfolioCovenantConfigFullPropertyKey={fullPropertyKey}
                portfolioCovenant={portfolioCovenant}
                sidebarState={{
                  isSidebarOpen: isNavigationSidebarOpen,
                  onSidebarOpen: onNavigationSidebarOpen,
                  onSidebarClose: onNavigationSidebarClose,
                }}
              />
            </GridItem>
            <GridItem colSpan={contentGridColspan} pl="40px">
              {children}
            </GridItem>
          </Grid>
        </Box>
      </Box>
    </>
  );
};
